/**
 * ControlMeasuresStep — "Medidas de Controle" step of the RiskApplicationModal stepper.
 *
 * Two sections:
 *  - Existentes: checklists (Administrativa / Engenharia) from the shared vocabulary,
 *    each with a free-text "Outros" field, plus a checklist of EPIs in use (from the
 *    risk's active RiskProtections).
 *  - Propostas: 3 free-text multiline fields.
 */

import React from 'react';
import {
  Stack,
  Box,
  Divider,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material';
import { Controller, useFormContext, type Control } from 'react-hook-form';
import {
  EXISTING_CONTROL_MEASURES,
  EXISTING_CONTROL_MEASURE_CATEGORY_LABELS,
  type ExistingControlMeasureCategory,
} from '../../../types/controlMeasures';
import type { StepperFormValues } from '../RiskApplicationModal';

export interface ControlMeasuresEpiOption {
  id: number;
  nome: string;
  metodo: string;
  atenuacao?: string | null;
  /** true quando o EPI foi selecionado antes mas não está mais entre as proteções ativas do risco. */
  orphaned?: boolean;
}

interface ControlMeasuresStepProps {
  epiOptions: ControlMeasuresEpiOption[];
  disabled: boolean;
}

// ============================================================================
// Existing-measures checklist (Administrativa / Engenharia) + "Outros"
// ============================================================================

interface MeasureChecklistProps {
  category: ExistingControlMeasureCategory;
  control: Control<StepperFormValues>;
  disabled: boolean;
}

const CHECKLIST_FIELD_NAME: Record<ExistingControlMeasureCategory, 'existingAdministrativeMeasures' | 'existingEngineeringMeasures'> = {
  ADMINISTRATIVE: 'existingAdministrativeMeasures',
  ENGINEERING: 'existingEngineeringMeasures',
};

const OTHER_FIELD_NAME: Record<ExistingControlMeasureCategory, 'existingAdministrativeOther' | 'existingEngineeringOther'> = {
  ADMINISTRATIVE: 'existingAdministrativeOther',
  ENGINEERING: 'existingEngineeringOther',
};

const MeasureChecklist: React.FC<MeasureChecklistProps> = ({ category, control, disabled }) => {
  const options = EXISTING_CONTROL_MEASURES[category];
  const fieldName = CHECKLIST_FIELD_NAME[category];
  const otherFieldName = OTHER_FIELD_NAME[category];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {EXISTING_CONTROL_MEASURE_CATEGORY_LABELS[category]}
      </Typography>
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => {
          const selected = field.value;

          const toggle = (measure: string) => {
            if (selected.includes(measure)) {
              field.onChange(selected.filter((m) => m !== measure));
            } else {
              field.onChange([...selected, measure]);
            }
          };

          return (
            <FormGroup>
              {options.map((measure) => (
                <FormControlLabel
                  key={measure}
                  control={
                    <Checkbox
                      checked={selected.includes(measure)}
                      onChange={() => toggle(measure)}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label={measure}
                />
              ))}
            </FormGroup>
          );
        }}
      />
      <Controller
        name={otherFieldName}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            label={`Outros (${EXISTING_CONTROL_MEASURE_CATEGORY_LABELS[category]})`}
            size="small"
            fullWidth
            disabled={disabled}
            sx={{ mt: 1 }}
          />
        )}
      />
    </Box>
  );
};

// ============================================================================
// Existing EPIs checklist
// ============================================================================

interface EpiChecklistProps {
  epiOptions: ControlMeasuresEpiOption[];
  control: Control<StepperFormValues>;
  disabled: boolean;
}

const formatEpiLabel = (o: ControlMeasuresEpiOption): string =>
  `${o.nome} — C.A. ${o.metodo}${o.atenuacao ? ` · ${o.atenuacao}` : ''}${o.orphaned ? ' (não vinculado ao risco)' : ''}`;

const EpiChecklist: React.FC<EpiChecklistProps> = ({ epiOptions, control, disabled }) => {
  if (epiOptions.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          EPIs em uso
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Nenhum EPI vinculado a este risco.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        EPIs em uso
      </Typography>
      <Controller
        name="existingEpiIds"
        control={control}
        render={({ field }) => {
          const selected = field.value;

          const toggle = (epiId: number) => {
            if (selected.includes(epiId)) {
              field.onChange(selected.filter((id) => id !== epiId));
            } else {
              field.onChange([...selected, epiId]);
            }
          };

          return (
            <FormGroup>
              {epiOptions.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      checked={selected.includes(option.id)}
                      onChange={() => toggle(option.id)}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      color={option.orphaned ? 'warning.main' : 'text.primary'}
                    >
                      {formatEpiLabel(option)}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          );
        }}
      />
    </Box>
  );
};

// ============================================================================
// Step component
// ============================================================================

export const ControlMeasuresStep: React.FC<ControlMeasuresStepProps> = ({ epiOptions, disabled }) => {
  const { control } = useFormContext<StepperFormValues>();

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Medidas Existentes
        </Typography>
        <Stack spacing={2}>
          <MeasureChecklist category="ADMINISTRATIVE" control={control} disabled={disabled} />
          <MeasureChecklist category="ENGINEERING" control={control} disabled={disabled} />
          <EpiChecklist epiOptions={epiOptions} control={control} disabled={disabled} />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Medidas Propostas
        </Typography>
        <Stack spacing={2}>
          <Controller
            name="proposedAdministrative"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Administrativas propostas"
                multiline
                minRows={2}
                fullWidth
                disabled={disabled}
              />
            )}
          />
          <Controller
            name="proposedEngineering"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Engenharia / EPC propostas"
                multiline
                minRows={2}
                fullWidth
                disabled={disabled}
              />
            )}
          />
          <Controller
            name="proposedEpi"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="EPIs propostos"
                multiline
                minRows={2}
                fullWidth
                disabled={disabled}
              />
            )}
          />
        </Stack>
      </Box>
    </Stack>
  );
};

export default ControlMeasuresStep;
